import type { AxiosInstance } from 'axios'
import { wrapApiCall } from '@/lib/axios'
import { deserialize } from 'ts-jackson'
import { PageInfo, PaginationOpt, PaginationResponse } from '../model'
import { Receipt } from './model'

export const ReceiptService = (client: AxiosInstance) => {
  return {
    async getReceipts(
      opt: PaginationOpt & {
        yearMonth?: string
        memberId?: string
        /**
         * Drop product sales paid with credits (QTY-1993). They belong in the
         * member's Credit Transactions tab; Receipts is for money actually
         * taken. Off unless asked — the branch-wide /payments list still wants
         * every receipt. Omitted rather than sent as `false` so the request
         * stays byte-identical to what it was before this flag existed.
         */
        excludeCreditSales?: boolean
      }
    ): Promise<PaginationResponse<Receipt>> {
      const params = {
        page: opt.page,
        pageSize: opt.pageSize,
        yearMonth: opt.yearMonth,
        query: opt.query,
        memberId: opt.memberId,
        excludeCreditSales: opt.excludeCreditSales ? true : undefined,
      }
      const { data } = await wrapApiCall(() =>
        client.get<{ data?: Record<string, unknown>[]; pagination: Record<string, unknown> }>(
          '/receipt',
          { params }
        )
      )
      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: data?.data?.map((item: Record<string, unknown>) => deserialize(item, Receipt)) || [],
      }
    },
    // NOTE: there is no GET /receipt/:id on the backend (only list / cancel /
    // pdf / send-email) — a detail view must render from the list row payload,
    // which already carries every field of the Receipt model.
    async getReceiptPdf(id: string | number): Promise<Blob> {
      const { data } = await wrapApiCall(() =>
        client.get<Blob>(`/receipt/${id}/pdf`, {
          responseType: 'blob',
          headers: { Accept: 'application/pdf' },
        })
      )
      return data
    },
    async getReceiptPdfSmall(id: string | number): Promise<Blob> {
      const { data } = await wrapApiCall(() =>
        client.get<Blob>(`/receipt/${id}/small`, {
          responseType: 'blob',
          headers: { Accept: 'application/pdf' },
        })
      )
      return data
    },
    async cancelReceipt(id: string | number, note: string): Promise<Receipt> {
      const { data } = await wrapApiCall(() =>
        client.patch<Record<string, unknown>>(`/receipt/${id}/cancel`, { note })
      )
      return deserialize(data, Receipt)
    },
  }
}
